/* Pricing is enforced by Shopify discounts. This UI only selects real variants. */
(() => {
  if (customElements.get('gpi-offer-builder')) return;
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class GpiOfferBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-offer-config]').textContent);
        this.selected = this.config.groups.map(() => ({ product: null, options: [], variant: null }));
        this.current = 0;
        this.busy = false;
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.config.currency });
        this.mount();
      } catch (error) {
        const message = el('p', 'gpi-empty-state', 'The offer selector could not load. Please refresh the page or shop individual products.');
        this.append(message);
        console.error('GPI offer selector:', error);
      }
    }
    mount() {
      this.config.groups.forEach((group, index) => {
        const tab = el('button', 'gpi-step-tab');
        tab.type = 'button'; tab.id = `gpi-tab-${this.dataset.sectionId}-${index}`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', `gpi-panel-${this.dataset.sectionId}-${index}`);
        tab.append(el('span', 'gpi-step-number', String(index + 1)), el('span', '', group.label));
        tab.addEventListener('click', () => this.show(index));
        tab.addEventListener('keydown', event => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.config.groups.length) % this.config.groups.length;
          this.show(next); this.querySelectorAll('.gpi-step-tab')[next].focus();
        });
        this.querySelector('.gpi-step-tabs').append(tab);
        const panel = el('section', 'gpi-step-panel');
        panel.id = `gpi-panel-${this.dataset.sectionId}-${index}`;
        panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', tab.id);
        panel.append(el('h2', '', `${index + 1}. ${group.label}`), el('p', 'gpi-step-note', group.note));
        const variantArea = el('div', 'gpi-variant-area'); variantArea.hidden = true;
        panel.append(variantArea);
        const filters = el('div', 'gpi-product-filter');
        const search = el('input'); search.type = 'search'; search.placeholder = 'Search designs'; search.setAttribute('aria-label', `Search ${group.label.toLowerCase()} designs`);
        const stockLabel = el('label', 'gpi-stock-filter');
        const stock = el('input'); stock.type = 'checkbox'; stockLabel.append(stock, document.createTextNode('In stock only'));
        filters.append(search, stockLabel); panel.append(filters);
        const choices = el('div', 'gpi-product-choices');
        group.products.forEach(product => {
          const available = product.variants.some(v => v.available);
          const button = el('button', 'gpi-product-choice'); button.type = 'button'; button.dataset.productId = String(product.id); button.setAttribute('aria-pressed', 'false'); button.disabled = !available;
          if (product.image) { const image = el('img'); image.src = product.image; image.alt = ''; image.loading = 'lazy'; image.width = 240; image.height = 240; button.append(image); }
          button.append(el('span', 'gpi-product-name', product.title), el('span', 'gpi-product-stock', available ? 'Choose design & size' : 'Out of stock'));
          button.addEventListener('click', () => this.pickProduct(index, product));
          choices.append(button);
        });
        const empty = el('p', 'gpi-empty-state', group.products.length ? 'No designs match these filters.' : 'This collection is being prepared. Check back for new designs.');
        empty.hidden = group.products.length > 0;
        const filter = () => {
          let count = 0;
          [...choices.children].forEach((button, pi) => {
            const product = group.products[pi];
            const visible = product.title.toLocaleLowerCase().includes(search.value.toLocaleLowerCase()) && (!stock.checked || product.variants.some(v => v.available));
            button.hidden = !visible; if (visible) count++;
          });
          empty.hidden = count > 0;
        };
        search.addEventListener('input', filter); stock.addEventListener('change', filter);
        panel.append(choices, empty); this.querySelector('.gpi-step-panels').append(panel);
      });
      this.querySelector('[data-back]').addEventListener('click', () => this.show(this.current - 1));
      this.querySelector('[data-next]').addEventListener('click', () => {
        if (this.current < this.selected.length - 1) this.show(this.current + 1);
        else this.querySelector('.gpi-offer-summary').scrollIntoView({ behavior: 'auto', block: 'center' });
      });
      this.querySelector('[data-add]').addEventListener('click', () => this.add(false));
      this.querySelector('[data-buy]').addEventListener('click', () => this.add(true));
      this.querySelector('[data-builder-content]').hidden = false;
      this.show(0); this.summary();
      if (this.config.currency !== this.config.shopCurrency) this.status('This fixed-price offer is currently available in USD. Please select USD to continue.', true);
    }
    show(index) {
      if (index < 0 || index >= this.selected.length) return;
      this.current = index;
      this.querySelectorAll('.gpi-step-tab').forEach((tab, i) => { tab.setAttribute('aria-selected', String(index === i)); tab.tabIndex = index === i ? 0 : -1; });
      this.querySelectorAll('.gpi-step-panel').forEach((panel, i) => { panel.hidden = index !== i; });
      this.querySelector('[data-back]').disabled = index === 0 || this.busy;
      this.querySelector('[data-next]').textContent = index === this.selected.length - 1 ? 'Review your selection ↓' : 'Next item →';
      this.querySelector('[data-next]').disabled = !this.selected[index].variant || this.busy;
    }
    pickProduct(index, product) {
      const selected = this.selected[index];
      selected.product = product; selected.options = product.options.map(() => ''); selected.variant = null;
      const panel = this.querySelectorAll('.gpi-step-panel')[index];
      panel.querySelectorAll('.gpi-product-choice').forEach(button => { button.setAttribute('aria-pressed', String(button.dataset.productId === String(product.id))); });
      const area = panel.querySelector('.gpi-variant-area'); area.replaceChildren(); area.hidden = false;
      area.append(el('h3', '', product.title));
      const fields = el('div', 'gpi-variant-fields');
      product.options.forEach((name, oi) => {
        if (name === 'Title' && product.variants.length === 1 && product.variants[0].title === 'Default Title') { selected.options[oi] = product.variants[0].options[oi]; return; }
        const label = el('label', '', name); const select = el('select');
        select.dataset.optionIndex = String(oi);
        select.setAttribute('aria-label', `${this.config.groups[index].label}: ${name}`);
        const placeholder = el('option', '', `Choose ${name.toLowerCase()}`); placeholder.value = ''; select.append(placeholder);
        [...new Set(product.variants.map(v => v.options[oi]))].forEach(value => { const option = el('option', '', value); option.value = value; option.disabled = !product.variants.some(v => v.available && v.options[oi] === value); select.append(option); });
        select.addEventListener('change', () => {
          selected.options[oi] = select.value;
          if (!product.variants.some(v => v.available && v.options.every((value, vi) => !selected.options[vi] || value === selected.options[vi]))) {
            selected.options.forEach((value, vi) => { if (vi !== oi) selected.options[vi] = ''; });
          }
          this.updateOptionAvailability(index); this.resolveVariant(index);
        });
        label.append(select); fields.append(label);
      });
      area.append(fields, el('p', 'gpi-variant-message')); this.resolveVariant(index);
      area.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      area.querySelector('select')?.focus();
    }
    updateOptionAvailability(index) {
      const selected = this.selected[index];
      this.querySelectorAll('.gpi-step-panel')[index].querySelectorAll('.gpi-variant-fields select').forEach(select => {
        const oi = Number(select.dataset.optionIndex);
        select.value = selected.options[oi];
        [...select.options].forEach(option => {
          if (!option.value) return;
          option.disabled = !selected.product.variants.some(v => v.available && v.options[oi] === option.value && v.options.every((value, vi) => vi === oi || !selected.options[vi] || value === selected.options[vi]));
        });
      });
    }
    resolveVariant(index) {
      const selected = this.selected[index];
      const match = selected.product.variants.find(v => v.options.every((value, oi) => value === selected.options[oi]));
      selected.variant = match?.available ? match : null;
      const ownMessage = this.querySelectorAll('.gpi-step-panel')[index].querySelector('.gpi-variant-message');
      const incomplete = selected.options.some(v => !v);
      ownMessage.textContent = selected.variant ? `Selected: ${selected.variant.title}. Included in the complete offer.` : incomplete ? 'Choose each option to confirm this item.' : 'This combination is unavailable. Please choose another size or colour.';
      ownMessage.classList.toggle('is-error', !incomplete && !selected.variant);
      this.summary(); this.show(this.current);
    }
    summary() {
      const list = this.querySelector('.gpi-summary-items'); list.replaceChildren();
      this.selected.forEach((selected, index) => {
        const li = el('li');
        if (selected.product?.image) { const image = el('img'); image.src = selected.product.image; image.alt = ''; image.width = 54; image.height = 64; li.append(image); }
        const copy = el('div', 'gpi-summary-item-copy');
        copy.append(el('strong', '', selected.product?.title || this.config.groups[index].label), el('small', '', selected.variant?.title || 'Choose design and options'));
        const change = el('button', 'gpi-summary-change', selected.product ? 'Change selection' : 'Choose item'); change.type = 'button'; change.addEventListener('click', () => { this.show(index); this.querySelectorAll('.gpi-step-tab')[index].focus(); }); copy.append(change); li.append(copy); list.append(li);
        this.querySelectorAll('.gpi-step-tab')[index].classList.toggle('is-complete', !!selected.variant);
      });
      const count = this.selected.filter(s => s.variant).length;
      this.querySelector('.gpi-summary-count').textContent = `${count} of ${this.selected.length} items selected`;
      const ready = count === this.selected.length && this.config.enabled && this.config.currency === this.config.shopCurrency && !this.busy;
      this.querySelector('[data-add]').disabled = !ready; this.querySelector('[data-buy]').disabled = !ready;
      const saving = this.querySelector('.gpi-summary-saving');
      const regular = this.selected.reduce((sum, s) => sum + (s.variant?.price || 0), 0);
      saving.hidden = count !== this.selected.length || regular <= this.config.price || this.config.currency !== this.config.shopCurrency;
      saving.textContent = `Save ${this.money.format((regular - this.config.price) / 100)} compared with buying these items individually.`;
    }
    status(message, error = false) { const node = this.querySelector('.gpi-builder-status'); node.textContent = message; node.classList.toggle('is-error', error); }
    async request(path, body) {
      const response = await fetch(this.config.root + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const data = await response.json(); if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'The cart could not be updated. Please try again.'); return data;
    }
    async syncAppDiscounts() {
      if (typeof window.bndlr?.updateCartDiscounts !== 'function') return;
      await new Promise(resolve => {
        let timer;
        const finish = () => { clearTimeout(timer); document.removeEventListener('bundler:total_cart_values', finish); resolve(); };
        document.addEventListener('bundler:total_cart_values', finish, { once: true });
        timer = setTimeout(finish, 7000);
        try { window.bndlr.updateCartDiscounts(); } catch (error) { finish(); }
      });
    }
    async applyBundleCode(cart) {
      if (typeof window.bndlr?.getCheckoutInfo !== 'function') throw new Error('Offer pricing is still loading. Please try again in a moment.');
      const info = await new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Offer pricing took too long. Please try again.')), 20000);
        window.bndlr.getCheckoutInfo(data => { clearTimeout(timer); resolve(data); });
      });
      if (!info?.can_apply_discount || typeof info.code !== 'string' || !info.code) {
        const bundles = typeof window.bndlr.getBundles === 'function' ? window.bndlr.getBundles() : [];
        console.warn('GPI offer pricing unavailable', JSON.stringify({ bundleId: this.config.bundleId, knownBundle: Array.isArray(bundles) && bundles.some(bundle => String(bundle.id) === this.config.bundleId), responseKeys: Object.keys(info || {}), canApply: info?.can_apply_discount, statusCode: info?.status_code, hasCode: !!info?.code }));
        throw new Error('The offer discount could not be confirmed. Please try again.');
      }
      const previousCode = sessionStorage.getItem('gpi:bundle-code');
      const codes = (cart.discount_codes || []).map(discount => discount.code).filter(code => code && code !== previousCode);
      codes.push(info.code);
      const updated = await this.request('cart/update.js', { discount: [...new Set(codes)].join(',') });
      sessionStorage.setItem('gpi:bundle-code', info.code);
      return updated;
    }
    async removeSelection(token) {
      const cart = await this.request('cart.js');
      const updates = {};
      cart.items.filter(item => item.properties?._gpi_offer_group === token).forEach(item => { updates[item.key] = 0; });
      if (Object.keys(updates).length) await this.request('cart/update.js', { updates });
    }
    async add(buyNow) {
      if (this.busy || !this.config.enabled || this.selected.some(s => !s.variant) || this.config.currency !== this.config.shopCurrency) return;
      this.busy = true; this.summary(); this.show(this.current); this.status('Checking availability and adding your complete selection…');
      window.gpiOfferCartBusy = true;
      const token = `${this.config.key}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let attemptedAdd = false;
      try {
        const before = await this.request('cart.js');
        if (before.items.some(item => item.properties?._gpi_offer_key === this.config.key)) throw new Error('This offer is already in your cart. Additional items can be purchased individually.');
        attemptedAdd = true;
        await this.request('cart/add.js', { items: this.selected.map((s, index) => {
          const sectionId = this.config.groups[index].bundleSectionId;
          return { id: s.variant.id, quantity: 1, properties: { Offer: this.config.title, '_gpi_offer_key': this.config.key, '_gpi_offer_group': token, '_gpi_offer_step': String(index + 1), '_bundler_id': this.config.bundleId, '_bundle_section_id': sectionId, [`_bundle_section_${sectionId}`]: '' } };
        }) });
        let after = await this.request('cart.js');
        if (after.total_price > before.total_price + this.config.price) {
          await this.syncAppDiscounts();
          after = await this.request('cart.js');
          if (after.total_price > before.total_price + this.config.price) after = await this.applyBundleCode(after);
        }
        if (after.total_price > before.total_price + this.config.price) {
          throw new Error('The offer price could not be confirmed. Your selection was not kept in the cart. Please try again or contact customer support.');
        }
        this.status('Your complete offer has been added.');
        window.location.assign(this.config.root + (buyNow ? 'checkout' : 'cart'));
      } catch (error) {
        if (attemptedAdd) {
          try { await this.removeSelection(token); }
          catch (cleanupError) { this.status('The cart could not be confirmed. Please review your cart before trying again.', true); return; }
        }
        this.status(error.message || 'Your selection could not be added. Please try again.', true);
      } finally { this.busy = false; window.gpiOfferCartBusy = false; this.summary(); this.show(this.current); document.dispatchEvent(new Event('gpi:cart-updated')); }
    }
  }
  customElements.define('gpi-offer-builder', GpiOfferBuilder);
})();
